import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';
import { TextInput } from './inputs';

export interface Command {
  id: string;
  label: string;
  group: string;
  hint?: string;
  run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** the static commands (pages, actions) */
  commands: Command[];
  /** the dynamic ones for a typed text (documents, templates, strings … through the search endpoints), debounced */
  search?: (text: string, signal: AbortSignal) => Promise<Command[]>;
}

/** Ctrl+K: pages and, as the user types, documents by id / unnyId / name, templates, strings, assets, products (Q-D-32). */
export function CommandPalette({ open, onClose, commands, search }: CommandPaletteProps) {
  const [text, setText] = useState('');
  const [found, setFound] = useState<Command[]>([]);
  const [active, setActive] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setText('');
      setFound([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !search || text.trim().length < 2) {
      setFound([]);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      search(text.trim(), controller.signal).then(
        (r) => {
          if (!controller.signal.aborted) {
            setFound(r);
            setSearching(false);
          }
        },
        () => {
          if (!controller.signal.aborted) setSearching(false);
        },
      );
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text, open, search]);

  const items = useMemo(() => {
    const q = text.trim().toLowerCase();
    const statics = q === '' ? commands : commands.filter((c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q));
    return [...statics, ...found];
  }, [commands, found, text]);

  useEffect(() => setActive(0), [items.length, text]);

  const run = (c: Command) => {
    onClose();
    c.run();
  };

  let lastGroup = '';
  return (
    <Modal title="Go to…" open={open} onClose={onClose} className="palette" initialFocus="none">
      <TextInput
        ref={inputRef}
        role="combobox"
        aria-expanded
        aria-controls="palette-list"
        aria-activedescendant={items[active] ? `palette-${items[active].id}` : undefined}
        aria-label="Search pages and content"
        placeholder="Type a page, a document id or name, a template…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(items.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && items[active]) {
            e.preventDefault();
            run(items[active]!);
          }
        }}
      />
      <ul id="palette-list" role="listbox" className="palette-list">
        {items.map((c, i) => {
          const header = c.group !== lastGroup ? c.group : null;
          lastGroup = c.group;
          return (
            <li key={c.id} role="presentation">
              {header && <div className="palette-group">{header}</div>}
              <div id={`palette-${c.id}`} role="option" aria-selected={i === active} className={`palette-item ${i === active ? 'active' : ''}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => run(c)}>
                <span>{c.label}</span>
                {c.hint && <span className="muted small">{c.hint}</span>}
              </div>
            </li>
          );
        })}
        {searching && <li className="palette-group">Searching…</li>}
        {!searching && items.length === 0 && <li className="palette-group">Nothing matches</li>}
      </ul>
    </Modal>
  );
}
